import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Badge, Popover } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { OLD_SYSTEM_HOST, ACCOUNT_VERSION } from '../config'
import {
  getGlobalConfig,
  getUnreadMessageCount,
  getWechatKanBanSenceQrCode
} from '@/services/mendian'
import { PopRobot } from './robot'
import { QRCodeModal } from './qrcode-modal'
import styles from './popmenu.module.less'

const QRCode = require('qrcode.react')

interface MenuComplexContent {
  type: 'qrcode';
  text: string;
  width: number;
  height: number;
  name: string; // 名称, App下载
}

interface MenuOption {
  id: number;
  isVisible: boolean;
  text: string;
  dot?: boolean;
  link?: string;
  popover?: boolean;
  content?: string | MenuComplexContent;
  handler?: () => void;
  extraClass?: string; // 附加css class
}

const PopMenuBtn = ({ menuOption }: { menuOption: MenuOption }) => {
  const { extraClass, dot, popover, text, content, link, handler } = menuOption
  const btnClass = classNames(styles.menuBtn, extraClass)
  let contentComponent
  const contentType = typeof content
  switch (contentType) {
    case 'string':
      contentComponent = content
      break
    case 'object':
      // eslint-disable-next-line no-case-declarations
      const objContent = content as MenuComplexContent
      if (objContent.type === 'qrcode') {
        contentComponent = (
          <div className={styles.qrCodeBox}>
            <QRCode value={objContent.text} size={objContent.width} />
            <div className={styles.qrCodeName}>{objContent.name}</div>
          </div>
        )
      }
      break
  }
  const onClickBtn = () => {
    if (typeof handler === 'function') {
      handler()
    }
    if (typeof link === 'string') {
      window.open(link)
    }
  }
  return (
    <div className={btnClass} onClick={onClickBtn}>
      {dot && <Badge className={styles.menuBtnBadge} status={'error'} />}
      {popover
        ? (
        <Popover
          trigger={'hover'}
          placement={'left'}
          content={contentComponent}
        >
          {text}
        </Popover>
          )
        : (
            text
          )}
    </div>
  )
}

/**
 * 当页面滚动到底部，页脚(Footer) 进入窗口时，浮动菜单需要向上移动，保持和Footer的距离
 * 这个 hook 返回正确的浮动菜单距离窗口底部的距离
 * 正常情况 24px
 * Footer出现时加上Footer当前在窗口内的高度（最多124）
 */
const useFooterOffset = () => {
  const getInitOffset = () => {
    if (
      document.scrollingElement &&
      document.scrollingElement.scrollHeight <= window.innerHeight
    ) {
      return 124
    } else {
      return 24
    }
  }
  const [offsetBottom, setOffsetBottom] = useState<number>(getInitOffset)
  useEffect(() => {
    const onScroll = () => {
      requestAnimationFrame(() => {
        if (document.scrollingElement) {
          const offset =
            window.scrollY +
            window.innerHeight -
            (document.scrollingElement.scrollHeight - 100)
          if (offset > 0) {
            setOffsetBottom(offset + 24)
          } else {
            setOffsetBottom(24)
          }
        }
      })
    }
    window.addEventListener('scroll', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])
  return {
    offsetBottom
  }
}

const usePopMenus = (basicMenu: MenuOption[]) => {
  const [popMenus, setPopMenus] = useState<MenuOption[]>(basicMenu)
  useEffect(() => {
    getUnreadMessageCount().then(res => {
      const { list = [] } = res || {}
      if (list.some((msgItem: { messageCount: number }) => msgItem.messageCount > 0)) {
        const messageMenuIndex = popMenus.findIndex(menu => menu.id === 2)
        const updatedMenus = popMenus.slice()
        updatedMenus[messageMenuIndex].dot = true
        setPopMenus(updatedMenus)
      }
    })
    getGlobalConfig().then(res => {
      const { comVersion = 3, themeInfo = {} } = res || {}
      const { customerServicePhone, qrCodeContent } = themeInfo as any
      const additionMenus = [
        {
          id: 4,
          isVisible:
            ACCOUNT_VERSION.TASTE_VERSION === comVersion &&
            ACCOUNT_VERSION.NET_WORKS_VERSION !== comVersion,
          text: '切换',
          popover: true,
          content: customerServicePhone
        },
        {
          id: 5,
          isVisible: ACCOUNT_VERSION.NET_WORKS_VERSION !== comVersion,
          text: '下载',
          popover: true,
          content: {
            type: 'qrcode',
            text: qrCodeContent || 'http://jikejia.cn/app/download',
            width: 120,
            height: 120,
            name: 'App下载'
          } as MenuComplexContent
        },
        {
          id: 7,
          isVisible: ACCOUNT_VERSION.NET_WORKS_VERSION !== comVersion,
          text: '客服',
          popover: true,
          content: customerServicePhone
        }
      ]
      const updatedMenus = popMenus.slice()
      updatedMenus.splice(popMenus.length - 1, 0, ...additionMenus)
      setPopMenus(updatedMenus)
    })
  }, [])
  return {
    popMenus
  }
}

const MetisPopMenu = () => {
  const [isMoveOut, setIsMoveOut] = useState(false)
  const [isQrModalVisible, setQrModalVisible] = useState(false)
  const { offsetBottom } = useFooterOffset()
  const { popMenus } = usePopMenus([
    {
      id: 1,
      isVisible: location.hostname.includes('mendian'),
      text: '智能客服',
      link: `${OLD_SYSTEM_HOST}/customerService/index`,
      extraClass: styles.machineBtn
    },
    {
      id: 122,
      isVisible: true,
      text: '移动数据',
      handler: () => {
        setQrModalVisible(true)
        // TODO: 埋点
        // window.loggerAction.sendpv({ action: 'mendian_daily_report_wechat_qr' })
      },
      extraClass: styles.squareBtn
    },
    {
      id: 2,
      isVisible: true,
      text: '消息',
      link: `${OLD_SYSTEM_HOST}/notice/toNoticeCenter`
    },
    {
      id: 3,
      isVisible: true,
      text: '帮助',
      link: `${OLD_SYSTEM_HOST}/help/helpList`
    },
    {
      id: 4,
      isVisible: true,
      text: '资质公示',
      link: window.location.host.includes('qiye.anjuke.com') ? '//h5.anjuke.com/broker/policy/store-vip-qualifications-rule-new' : '//h5.anjuke.com/broker/policy/store-vip-qualifications-rule',
      extraClass: styles.qualifications
    }
  ])
  const onGetQrCode = () => {
    return getWechatKanBanSenceQrCode().then(res => {
      return {
        qrCodeUrl: res.qrCodeUrl
      }
    })
  }
  const btnList = popMenus
    .filter(menu => menu.isVisible)
    .map(btn => {
      return <PopMenuBtn key={btn.id} menuOption={btn} />
    })
  const toggleMoveOut = () => {
    setIsMoveOut(!isMoveOut)
  }
  const popClass = classNames(styles.pop, {
    [styles.moveout]: isMoveOut,
    [styles.movein]: !isMoveOut
  })
  return (
    <>
      <div className={popClass} style={{ bottom: offsetBottom }}>
        <PopRobot />
        <div className={styles.box}>
          <div className={styles.handleBar} onClick={toggleMoveOut}>
            {isMoveOut
              ? (
              <LeftOutlined className={styles.handleIcon} />
                )
              : (
              <RightOutlined className={styles.handleIcon} />
                )}
          </div>
          <div className={styles.menuBtnList}>{btnList}</div>
        </div>
      </div>
      <QRCodeModal
        slogan={'手机看数据 · 更快更简单'}
        onGetQrCode={onGetQrCode}
        onClose={() => setQrModalVisible(false)}
        visible={isQrModalVisible}
      />
    </>
  )
}

export { MetisPopMenu }
